import React, { useState, useEffect } from 'react';
import './App.css';

// Import Navbar component
import Navbar from './components/Navbar/Navbar';

// Import components
import Home from './pages/Home/Home';
import Products from './pages/Products/Products';
import Services from './pages/Services/Services';
import Details from './pages/Details/Details';
import About from './pages/About/About';
import Certificates from './pages/Certificates/Certificates';
import LoginSignup from './pages/LoginSignup/LoginSignup';
import ProductAdmin from './pages/ProductAdmin/ProductAdmin';
import AdminManagement from './pages/AdminManagement/AdminManagement';
import AdminOrders from './pages/AdminOrders/AdminOrders.js';
import AdminOrderDetails from './pages/AdminOrderDetails/AdminOrderDetails';
import Cart from './components/Cart/Cart';
import Checkout from './components/Checkout/Checkout';
import MyOrders from './pages/MyOrders/MyOrders';
import ProductComparison from './pages/ProductComparison/ProductComparison';

// ✅ NEW IMPORTS - Installation Management
import OrderTracking from './pages/Orders/OrderTracking';
import AdminInstallations from './pages/Orders/AdminInstallations.js';

// ✅ NEW IMPORTS - Chatbot
import ChatBot from './pages/ChatBot/ChatBot';
import FloatingChatButton from './components/FloatingChatButton/FloatingChatButton';

// ✅ NEW IMPORTS - Web Development, Hosting & Blog
import WebDevelopment from './pages/WebDevelopment/WebDevelopment';
import Blog from './pages/Blog/Blog';
import ReviewDialog from './components/ReviewDialog/ReviewDialog';
import AdminReviews from './pages/AdminReviews/AdminReviews';
import LoadingSpinner from './components/Common/LoadingSpinner';

// Import services
import ApiService from './services/api';

function App() {
  // State management
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Page data for navigation (details page, etc.)
  const [pageData, setPageData] = useState(null);

  // Navigation history for back button
  const [pageHistory, setPageHistory] = useState(['home']);

  // ✅ NEW STATE - Review Dialog
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  // Key trigger state to reset/remount login form
  const [loginResetKey, setLoginResetKey] = useState(0);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to browser back button
  useEffect(() => {
    const handleBrowserBack = (event) => {
      if (pageHistory.length > 1) {
        handleBack();
      }
    };

    window.addEventListener('popstate', handleBrowserBack);

    return () => {
      window.removeEventListener('popstate', handleBrowserBack);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageHistory]);

  // Authentication functions
  const checkAuthStatus = async () => {
    setIsLoading(true);

    try {
      const token = ApiService.getToken();
      const storedUser = ApiService.getUser();

      console.log('🔍 Checking auth status:', {
        hasToken: !!token,
        hasStoredUser: !!storedUser,
        storedUserEmail: storedUser?.email
      });

      if (token && storedUser) {
        try {
          const response = await ApiService.getProfile();

          console.log('📥 Profile response:', response);

          let userData = null;

          if (response.success && response.data) {
            userData = response.data.user || response.data;
          } else if (response.success && response.user) {
            userData = response.user;
          } else if (response.user) {
            userData = response.user;
          }

          if (userData) {
            setIsLoggedIn(true);
            setUser(userData);

            const userIsAdmin = userData.role === 'admin';
            setIsAdmin(userIsAdmin);

            if (userIsAdmin) {
              handleSetCurrentPage('admin-dashboard');
            } else {
              handleSetCurrentPage('home');
            }

            console.log('✅ Session restored:', {
              email: userData.email,
              role: userData.role,
              isAdmin: userIsAdmin
            });
          } else {
            throw new Error('Invalid user data in response');
          }

        } catch (profileError) {
          console.warn('⚠️ Profile fetch failed, session may be expired:', profileError.message);

          if (storedUser && storedUser.email) {
            console.log('ℹ️ Using cached user data (offline mode)');

            setIsLoggedIn(true);
            setUser(storedUser);

            const userIsAdmin = storedUser.role === 'admin';
            setIsAdmin(userIsAdmin);

            if (userIsAdmin) {
              handleSetCurrentPage('admin-dashboard');
            } else {
              handleSetCurrentPage('home');
            }
          } else {
            console.log('ℹ️ No valid session, starting as guest');
            ApiService.removeToken();
            setIsLoggedIn(false);
            setUser(null);
            setIsAdmin(false);
            handleSetCurrentPage('home');
          }
        }
      } else {
        console.log('ℹ️ No stored authentication found - browsing as guest');
        handleSetCurrentPage('home');
      }

    } catch (error) {
      console.error('❌ Auth check failed:', error);
      setIsLoggedIn(false);
      setUser(null);
      setIsAdmin(false);
      handleSetCurrentPage('home');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetCurrentPage = (page, data = null) => {
    // Add current page to history
    setPageHistory(prev => [...prev, page]);
    
    if (page === 'login') {
      setLoginResetKey(prev => prev + 1);
    }

    setCurrentPage(page);

    // Scroll to the top of the viewport on page change
    window.scrollTo(0, 0);

    // Set page data if provided
    if (data) {
      setPageData(data);
    }

    // Update URL without page reload
    window.history.pushState({ page, data }, '', `#${page}`);
  };

  const handleLogin = (userData) => {
    console.log('🔐 Handling login with user data:', userData);

    setIsLoggedIn(true);
    setUser(userData);

    const userIsAdmin = userData.role === 'admin';
    setIsAdmin(userIsAdmin);

    if (userIsAdmin) {
      handleSetCurrentPage('admin-dashboard');
      console.log('🔑 Admin user logged in, redirecting to admin dashboard');
    } else {
      handleSetCurrentPage('home');
      console.log('👤 Regular user logged in, redirecting to home');
      // ✅ Show review dialog for logged-in users
      setShowReviewDialog(true);
    }
  };

  const handleLogout = async () => {
    console.log('🚪 Logging out...');

    try {
      await ApiService.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    }

    ApiService.removeToken();
    setIsLoggedIn(false);
    setUser(null);
    setIsAdmin(false);
    handleSetCurrentPage('home');
    setPageData(null);
    setPageHistory(['home']);

    console.log('✅ Logged out successfully');
  };

  const handleBack = () => {
    if (pageHistory.length > 1) {
      const newHistory = [...pageHistory];
      newHistory.pop();

      const previousPage = newHistory[newHistory.length - 1];
      setPageHistory(newHistory);
      setCurrentPage(previousPage);

      window.history.pushState({ page: previousPage }, '', `#${previousPage}`);

      if (previousPage !== 'details') {
        setPageData(null);
      }
    } else {
      handleSetCurrentPage(isAdmin ? 'admin-dashboard' : 'home');
    }
  };

  const handleRelatedItemClick = (newItemId, newItemType) => {
    console.log('Navigating to related item:', newItemId, newItemType);
    handleSetCurrentPage('details', {
      itemId: newItemId,
      itemType: newItemType,
      previousPage: 'details'
    });
  };

  const handleLoginRequired = () => {
    handleSetCurrentPage('login');
  };

  // Admin page renderer
  const renderAdminPage = () => {
    switch (currentPage) {
      case 'admin-dashboard':
        return (
          <div className="admin-dashboard">
            <div className="dashboard-header">
              <h1>Admin Dashboard</h1>
              <p>Welcome, {user?.email || 'Admin'}</p>
            </div>

            <div className="dashboard-actions">
              <button
                className="btn btn-primary dashboard-btn"
                onClick={() => handleSetCurrentPage('product-admin')}
              >
                ➕ Add New Product/Service
              </button>
              <button
                className="btn btn-secondary dashboard-btn"
                onClick={() => handleSetCurrentPage('admin-management')}
              >
                📊 Manage Inventory
              </button>
              <button
                className="btn btn-info dashboard-btn"
                onClick={() => handleSetCurrentPage('admin-orders')}
              >
                📦 Manage Orders
              </button>
              {/* ✅ NEW BUTTON - Installation Management */}
              <button
                className="btn btn-success dashboard-btn"
                onClick={() => handleSetCurrentPage('admin-installations')}
              >
                🔧 Manage Installations
              </button>
              {/* ✅ NEW BUTTON - Reviews Management */}
              <button
                className="btn btn-info dashboard-btn"
                onClick={() => handleSetCurrentPage('admin-reviews')}
              >
                📝 Manage Reviews
              </button>
            </div>

            <div className="dashboard-stats">
              <div className="stat-card">
                <h3>Quick Actions</h3>
                <ul>
                  <li>
                    <button onClick={() => handleSetCurrentPage('product-admin')}>
                      Add New Product
                    </button>
                  </li>
                  <li>
                    <button onClick={() => handleSetCurrentPage('product-admin')}>
                      Add New Service
                    </button>
                  </li>
                  <li>
                    <button onClick={() => handleSetCurrentPage('admin-management')}>
                      View All Products
                    </button>
                  </li>
                  <li>
                    <button onClick={() => handleSetCurrentPage('admin-orders')}>
                      View All Orders
                    </button>
                  </li>
                  {/* ✅ NEW - Installation Quick Action */}
                  <li>
                    <button onClick={() => handleSetCurrentPage('admin-installations')}>
                      Manage Installations
                    </button>
                  </li>
                  {/* ✅ NEW - Reviews Quick Action */}
                  <li>
                    <button onClick={() => handleSetCurrentPage('admin-reviews')}>
                      Manage Reviews
                    </button>
                  </li>
                </ul>
              </div>

              <div className="stat-card">
                <h3>Navigation</h3>
                <ul>
                  <li>
                    <button onClick={() => handleSetCurrentPage('admin-dashboard')}>
                      Dashboard
                    </button>
                  </li>
                  <li>
                    <button onClick={() => {
                      setIsAdmin(false);
                      handleSetCurrentPage('home');
                    }}>
                      View User Site
                    </button>
                  </li>
                  <li>
                    <button onClick={handleLogout}>
                      Logout
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'product-admin':
        return <ProductAdmin setCurrentPage={handleSetCurrentPage} />;

      case 'admin-management':
        return <AdminManagement setCurrentPage={handleSetCurrentPage} />;

      case 'admin-orders':
        return <AdminOrders setCurrentPage={handleSetCurrentPage} setPageData={setPageData} />;

      case 'admin-order-details':
        return (
          <AdminOrderDetails
            orderId={pageData?.orderId}
            setCurrentPage={handleSetCurrentPage}
            onBack={() => handleSetCurrentPage('admin-orders')}
          />
        );

      case 'admin-installations':
        return <AdminInstallations setCurrentPage={handleSetCurrentPage} />;

      case 'admin-reviews':
        return <AdminReviews setCurrentPage={handleSetCurrentPage} />;

      case 'details':
        return (
          <Details
            itemId={pageData?.itemId}
            itemType={pageData?.itemType}
            setCurrentPage={handleSetCurrentPage}
            onBack={handleBack}
            onRelatedItemClick={handleRelatedItemClick}
            isLoggedIn={isLoggedIn}
            onLoginRequired={handleLoginRequired}
          />
        );

      default:
        handleSetCurrentPage('admin-dashboard');
        return null;
    }
  };

  // User page renderer
  const renderUserPage = () => {
    switch (currentPage) {
      case 'home':
        return (
          <Home
            setCurrentPage={handleSetCurrentPage}
            setPageData={setPageData}
          />
        );

      case 'products':
        return (
          <Products
            setCurrentPage={handleSetCurrentPage}
            setPageData={setPageData}
          />
        );

      case 'product-comparison':
        return (
          <ProductComparison
            pageData={pageData}
            setCurrentPage={handleSetCurrentPage}
            setPageData={setPageData}
          />
        );

      case 'services':
        return (
          <Services
            setCurrentPage={handleSetCurrentPage}
            setPageData={setPageData}
          />
        );

      case 'web-development':
        return (
          <WebDevelopment
            setCurrentPage={handleSetCurrentPage}
            setPageData={setPageData}
          />
        );

      case 'details':
        return (
          <Details
            itemId={pageData?.itemId}
            itemType={pageData?.itemType}
            setCurrentPage={handleSetCurrentPage}
            onBack={handleBack}
            onRelatedItemClick={handleRelatedItemClick}
            isLoggedIn={isLoggedIn}
            onLoginRequired={handleLoginRequired}
          />
        );

      case 'cart':
        if (!isLoggedIn) {
          alert('Please login to view your cart');
          handleSetCurrentPage('login');
          return null;
        }
        return (
          <Cart
            setCurrentPage={handleSetCurrentPage}
            setPageData={setPageData}
          />
        );

      case 'checkout':
        if (!isLoggedIn) {
          alert('Please login to checkout');
          handleSetCurrentPage('login');
          return null;
        }
        return (
          <Checkout
            setCurrentPage={handleSetCurrentPage}
            setPageData={setPageData}
            user={user}
            onBack={() => handleSetCurrentPage('cart')}
          />
        );

      case 'my-orders':
        if (!isLoggedIn) {
          alert('Please login to view your orders');
          handleSetCurrentPage('login');
          return null;
        }
        return (
          <MyOrders
            setCurrentPage={handleSetCurrentPage}
            user={user}
          />
        );

      case 'order-tracking':
        return (
          <OrderTracking
            orderNumber={pageData?.orderNumber}
            setCurrentPage={handleSetCurrentPage}
          />
        );

      case 'chatbot':
        return <ChatBot setCurrentPage={handleSetCurrentPage} />;

      case 'about':
        return <About isLoggedIn={isLoggedIn} user={user} />;

      case 'blog':
        return <Blog setCurrentPage={handleSetCurrentPage} />;

      case 'certificates':
        return <Certificates />;

      case 'contact':
        return (
          <div className="section">
            <div className="container">
              <h2 className="section-title">Contact Us</h2>
              <p className="section-subtitle">Get in touch for surveillance solutions.</p>
              {/* ✅ Review Button */}
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <button
                  className="view-reviews-btn"
                  onClick={() => setShowReviewDialog(true)}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" className="star-icon" style={{ verticalAlign: 'middle', display: 'inline-block' }}>
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  View Customer Reviews
                </button>
              </div>
              <div className="contact-info" style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                maxWidth: '600px',
                margin: '2rem auto'
              }}>
                <h3>Contact Information</h3>
                <p><strong>Email:</strong> <a href={`https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent('Info@transcontinentalnetwork.com')}`} target="_blank" rel="noopener noreferrer">Info@transcontinentalnetwork.com</a></p>
                <p><strong>Phone:</strong> <a href={`tel:+13476682160`} style={{ color: '#0066cc', textDecoration: 'none' }}>347.668.2160</a></p>
                <p><strong>Address:</strong> 160-04 Cross Bay Blvd. Howard Beach, NY 11414</p>
                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <iframe
                    title="Company Location"
                    src="https://maps.google.com/maps?q=160-04%20Cross%20Bay%20Blvd.%20Howard%20Beach%2C%20NY%2011414&t=&z=15&ie=UTF8&iwloc=&output=embed"
                    width="100%"
                    height="300"
                    style={{ border: 0, borderRadius: '8px', display: 'block' }}
                    allowFullScreen=""
                    loading="lazy"
                  ></iframe>
                </div>
                <p><strong>Hours:</strong> Mon-Fri 9AM-6PM, Sat 9AM-2PM</p>
              </div>
            </div>
          </div>
        );

      case 'login':
        return (
          <LoginSignup
            key={`login-${loginResetKey}`}
            setIsLoggedIn={setIsLoggedIn}
            setCurrentPage={handleSetCurrentPage}
            setUser={setUser}
            onLogin={handleLogin}
          />
        );

      default:
        return (
          <Home
            setCurrentPage={handleSetCurrentPage}
            setPageData={setPageData}
          />
        );
    }
  };

  if (isLoading) {
    return <LoadingSpinner type="full-page" message="Loading Transcontinental Network..." />;
  }

  // ADMIN FLOW
  if (isAdmin && isLoggedIn) {
    return (
      <div className="admin-app">
        <nav className="admin-nav">
          <div className="admin-nav-brand">
            <h2>TRANSCONTINENTAL NETWORK - Admin Panel</h2>
          </div>
          <div className="admin-nav-menu">
            <button
              className={`nav-btn ${currentPage === 'admin-dashboard' ? 'active' : ''}`}
              onClick={() => handleSetCurrentPage('admin-dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`nav-btn ${currentPage === 'product-admin' ? 'active' : ''}`}
              onClick={() => handleSetCurrentPage('product-admin')}
            >
              Add Items
            </button>
            <button
              className={`nav-btn ${currentPage === 'admin-management' ? 'active' : ''}`}
              onClick={() => handleSetCurrentPage('admin-management')}
            >
              Manage Inventory
            </button>
            <button
              className={`nav-btn ${currentPage === 'admin-orders' ? 'active' : ''}`}
              onClick={() => handleSetCurrentPage('admin-orders')}
            >
              Orders
            </button>
            {/* ✅ NEW NAV BUTTON - Installations */}
            <button
              className={`nav-btn ${currentPage === 'admin-installations' ? 'active' : ''}`}
              onClick={() => handleSetCurrentPage('admin-installations')}
            >
              Installations
            </button>
          </div>
          <div className="admin-nav-user">
            <span>Welcome, {user?.email || 'Admin'}</span>
            <button className="btn-logout" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </nav>

        <main className="admin-main-content">
          {renderAdminPage()}
        </main>
      </div>
    );
  }

  // USER FLOW
  return (
    <div className="user-app">
      <Navbar
        currentPage={currentPage}
        setCurrentPage={handleSetCurrentPage}
        isLoggedIn={isLoggedIn}
        onLogout={handleLogout}
        user={user}
      />

      <main className="user-main-content">
        {renderUserPage()}
      </main>

      {/* ✅ FLOATING CHATBOT BUTTON - Only show when not on chatbot page */}
      {currentPage !== 'chatbot' && (
        <FloatingChatButton
          onOpen={() => handleSetCurrentPage('chatbot')}
          isVisible={true}
        />
      )}

      {/* ✅ REVIEW DIALOG - Shows on first visit or when opened */}
      <ReviewDialog
        isOpen={showReviewDialog}
        onClose={() => setShowReviewDialog(false)}
        isLoggedIn={isLoggedIn}
        user={user}
      />

      <footer className="user-footer">
        <div className="footer-content">
          <div className="footer-logo">
            TRANSCONTINENTAL NETWORK
          </div>
          <div className="footer-tagline">
            Video Surveillance Equipment and Installation for Your Home or Business
          </div>
          <div className="footer-links">
            <button onClick={() => handleSetCurrentPage('home')}>Home</button>
            <span>|</span>
            <button onClick={() => handleSetCurrentPage('products')}>Products</button>
            <span>|</span>
            <button onClick={() => handleSetCurrentPage('services')}>Services</button>
            <span>|</span>
            <button onClick={() => handleSetCurrentPage('web-development')}>Web &amp; Hosting</button>
            <span>|</span>
            <button onClick={() => handleSetCurrentPage('about')}>About</button>
            <span>|</span>
            <button onClick={() => handleSetCurrentPage('certificates')}>Certificates</button>
            <span>|</span>
            <button onClick={() => handleSetCurrentPage('contact')}>Contact</button>
            {/* ✅ NEW FOOTER LINK - Order Tracking */}
            <span>|</span>
            <button onClick={() => handleSetCurrentPage('order-tracking')}>Track Order</button>
            {/* ✅ NEW FOOTER LINK - Chatbot */}
            <span>|</span>
            <button onClick={() => handleSetCurrentPage('chatbot')}>Chat</button>
            {/* ✅ NEW FOOTER LINK - Reviews */}
            <span>|</span>
            <button onClick={() => setShowReviewDialog(true)}>Reviews</button>
          </div>
          <div className="footer-copyright">
            &copy; {new Date().getFullYear()} Transcontinental Network. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;